import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import CustomerHeader from "../../components/CustomerHeader";
import { ChangePassword } from "../../components/ChangePassword";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { User2, MapPin, Trash2, Plus, LogOut, Package, History } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", city: "Bengaluru", pincode: "" });
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!user) return navigate("/login?role=customer");
    api.get("/me/addresses").then((r) => setAddresses(r.data)).catch((e) => { console.error("Load addresses failed:", e); toast.error("Couldn't load addresses"); });
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch((e) => console.error("Load orders failed:", e));
  }, [user, navigate]);

  const addAddr = async () => {
    if (!newAddr.line1 || newAddr.line1.length < 6) { toast.error("Enter a full address"); return; }
    try {
      const { data } = await api.post("/me/addresses", newAddr);
      setAddresses((p) => [...p, data]);
      setNewAddr({ label: "Home", line1: "", city: "Bengaluru", pincode: "" });
      toast.success("Address added");
    } catch { toast.error("Could not save"); }
  };

  const delAddr = async (id) => {
    try {
      await api.delete(`/me/addresses/${id}`);
      setAddresses((p) => p.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Delete address failed:", e);
      toast.error("Could not delete address");
    }
  };

  if (!user) return null;

  return (
    <div className="light-scope min-h-screen bg-slate-50" data-testid="profile-container">
      <CustomerHeader onCartClick={() => {}} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-white font-bold inline-flex items-center justify-center text-xl">
            {user.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-xl text-slate-900 truncate">{user.name}</div>
            <div className="text-sm text-slate-500 truncate">{user.email}</div>
          </div>
          <Button variant="outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="profile-logout">
            <LogOut size={14} className="mr-1.5" /> Logout
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-5">
          <div className="lg:col-span-2 space-y-5">
            <section className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-extrabold text-lg text-slate-900 flex items-center gap-2"><MapPin size={16} className="text-emerald-600" /> Saved addresses</h2>
                <div className="text-xs text-slate-500">{addresses.length} saved</div>
              </div>
              <div className="mt-3 space-y-2" data-testid="addresses-list">
                {addresses.length === 0 && <div className="text-slate-500 text-sm">No addresses saved yet.</div>}
                {addresses.map((a) => (
                  <div key={a.id} className="rounded-xl border border-slate-200 p-3 flex items-start gap-3" data-testid={`addr-row-${a.id}`}>
                    <MapPin size={14} className="text-slate-500 mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900">{a.label}</div>
                      <div className="text-sm text-slate-600 truncate">{a.line1}{a.city ? `, ${a.city}` : ""}{a.pincode ? ` - ${a.pincode}` : ""}</div>
                    </div>
                    <button onClick={() => delAddr(a.id)} className="text-slate-400 hover:text-rose-500" data-testid={`delete-addr-${a.id}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-slate-200 pt-4">
                <div className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Plus size={14} /> Add new address</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>Label</Label><Input value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} data-testid="new-addr-label" /></div>
                  <div><Label>Pincode</Label><Input value={newAddr.pincode} onChange={(e) => setNewAddr({ ...newAddr, pincode: e.target.value })} data-testid="new-addr-pin" /></div>
                  <div className="sm:col-span-2"><Label>Address</Label><Textarea rows={2} value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} data-testid="new-addr-line1" /></div>
                  <div><Label>City</Label><Input value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} data-testid="new-addr-city" /></div>
                </div>
                <Button className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={addAddr} data-testid="save-new-addr-btn">Save address</Button>
              </div>
            </section>
            <ChangePassword />
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2">
                <History size={16} className="text-emerald-600" />
                <div className="font-extrabold text-slate-900">Recent orders</div>
              </div>
              <div className="mt-3 space-y-2" data-testid="profile-orders">
                {orders.slice(0, 5).map((o) => (
                  <button key={o.order_id} onClick={() => navigate(`/orders/${o.order_id}`)} className="w-full text-left rounded-xl border border-slate-200 p-3 hover:border-slate-400 transition">
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-sm">{o.shop_name}</div>
                      <div className="text-xs text-slate-500">#{o.order_id.slice(-6).toUpperCase()}</div>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
                    <div className="mt-1 flex items-center justify-between">
                      <div className="text-[11px] uppercase font-bold tracking-widest text-emerald-700">{o.status}</div>
                      <div className="font-bold text-sm">₹{o.total.toFixed(2)}</div>
                    </div>
                  </button>
                ))}
                {orders.length === 0 && <div className="text-sm text-slate-500">No orders yet.</div>}
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
